import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandModel, ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SampleRequestForm, { SampleRequestFormStyles } from "@ucs/Components/AuthenticatedRequestForm/SampleRequestForm";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@ucs/Overrides/Components/ProductPartNumbers";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC, useContext, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        isAuthenticated: state.context.session!.isAuthenticated,
        state,
    };
};

interface OwnProps {
    sampleProduct: ProductModel;
    isModalOpen: boolean;
    toggleModal: Function;
    extendedStyles?: AuthenticatedSampleRequestFormStyles;
    redirectOnSubmit :  LinkFieldValue;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface AuthenticatedSampleRequestFormStyles {
    formModal?: ModalPresentationProps;
    wrapper?: InjectableCss;
    productSection?: InjectableCss;
    img?: LazyImageProps;
    descriptionSection?: InjectableCss;
    brandLabel?: TypographyProps;
    productName?: TypographyProps;
    infoMessage?: TypographyProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    sampleRequestFormStyles: SampleRequestFormStyles;
}

export const authenticatedSampleRequestFormStyles: AuthenticatedSampleRequestFormStyles = {
    formModal: {
        size: 800,
        cssOverrides: {
            modalContent: css`
                padding: 0 20px 10px;
                background-color: ${UCSColors.gray08};
            `,
            modalTitle: css`
                padding: 20px;
                border-bottom: none;
                background-color: ${UCSColors.gray08};
            `,
            titleButton: css`
                width: 20px;
                height: 20px;
                min-height: 20px;
                padding: 0;
                position: relative;
                background-color: transparent !important;
                background: transparent !important;
                border: none;
                & span {
                    position: absolute !important;
                    top: 0 !important;
                    left: -1px;
                    width: 18px;
                    height: 18px;
                }
                & svg {
                    width: 18px;
                    height: 18px;
                }
                &:hover {
                    background-color: transparent !important;
                    background: transparent !important;
                    border: none;
                }
            `,
            modalContainer: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            margin-top: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 100%;
                        `,
                        css`
                            margin-top: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 100%;
                        `,
                        css`
                            margin-top: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 100%;
                        `,
                    ]);
                }}
            `,
            modalBody: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            height: 100vh;
                            width: 100vw;
                            min-height: 350px;
                            box-shadow: ${theme?.shadows[3]};
                        `,
                        css`
                            height: auto;
                            max-height: calc(100% - 40px);
                            margin: 25px;
                            max-width: 540px;
                            box-shadow: ${theme?.shadows[3]};
                        `,
                        css`
                            height: auto;
                            max-height: calc(100% - 90px);
                            margin: 45px;
                            max-width: 660px;
                            box-shadow: ${theme?.shadows[3]};
                        `,
                    ]);
                }}
            `,
        },
    },
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            form > * {
                margin-bottom: 15px;
            }
        `,
    },
    productSection: {
        css: css`
            width: 100%;
            display: flex;
            gap: 10px;
            align-items: flex-start;
            margin-bottom: 20px;
            background-color: #fff;
            padding: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        align-items: stretch;
                    `,
                    css`
                        align-items: stretch;
                    `,
                ]);
            }}
        `,
    },
    img: {
        width: "120px",
        height: "120px",
        css: css`
            box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
            & img {
                height: 100%;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100px;
                        min-width: 100px;
                        min-height: 100px;
                        height: auto;
                    `,
                    css`
                        width: 100px;
                        min-width: 100px;
                        min-height: 100px;
                        height: auto;
                    `,
                    css`
                        width: 100px;
                        height: 100px;
                    `,
                ]);
            }}
        `,
    },
    productName: {
        size: 18,
        color: UCSColors.gray00,
        weight: 500,
        as: "p",
        css: css`
            margin-bottom: 8px;
            line-height: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 16px;
                        margin-bottom: 6px;
                    `,
                    css`
                        font-size: 16px;
                        margin-bottom: 6px;
                    `,
                ]);
            }}
        `,
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                min-width: 200px;
            `,
        },
        erpNumberGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 13px;
                        `,
                        css`
                            font-size: 13px;
                        `,
                    ]);
                }}
            `,
        },
        erpNumberLabelText: {
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
                min-width: fit-content;
            `,
        },
        erpNumberValueText: {
            css: css`
                min-width: fit-content;
            `,
        },
        customerNameGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
            `,
        },
        customerNameLabelText: {
            variant: "p",
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
            `,
        },
        manufacturerItemGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
            `,
        },
        manufacturerItemLabelText: {
            variant: "p",
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
            `,
        },
    },
    descriptionSection: {
        css: css`
            margin-right: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-right: 0;
                    `,
                    css`
                        margin-right: 0;
                    `,
                    css`
                        margin-right: 15px;
                    `,
                ]);
            }}
            @media only screen and (max-width: 400px) {
                max-width: 57%;
            }
        `,
    },
    brandLabel: {
        weight: 700,
        transform: "uppercase",
        css: css`
            font-size: 13px;
            color: ${UCSColors.gray04} !important;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
        `,
    },
    sampleRequestFormStyles: {},
    infoMessage: {
        variant: "p",
        weight: 500,
        color: UCSColors.tertiaryRR00,
        css: css`
            margin-top: 0.8em;
            margin-bottom: 0;
        `,
    },
};

const AuthenticatedSampleRequestForm: FC<Props> = ({ extendedStyles, isModalOpen, toggleModal, sampleProduct, state , redirectOnSubmit}) => {
    const [styles] = useState(() => mergeToNew(authenticatedSampleRequestFormStyles, extendedStyles));

    const getImageUrl = () => {
        if (sampleProduct && sampleProduct.images && sampleProduct.images.length > 0) {
            let img = sampleProduct.images.find((image: ImageModel) => image.name.toLowerCase().includes("_swatch"));
            if (!img) {
                img = sampleProduct.images.find((image: ImageModel) => image.name.toLowerCase().includes("_alt1"));
            }
            if (!img) {
                img = sampleProduct.images.find((image: ImageModel) => image.name.toLowerCase().includes("_alt2"));
            }
            if (img) {
                return img.smallImagePath || img.mediumImagePath;
            }
        }
        return sampleProduct.smallImagePath || sampleProduct.mediumImagePath;
    };

    const productBrand: BrandModel | null = useContext(ProductContext).product.brand;
    const productSku: string = sampleProduct.productNumber;
    const productImage: string = getImageUrl();
    const productName: string = sampleProduct.productTitle;

    const modalCloseHandler = () => {
        toggleModal();
    };

    return (
        <Modal
            {...styles.formModal}
            headline="Order Sample"
            isOpen={isModalOpen}
            handleClose={modalCloseHandler}
            closeOnScrimClick={false}
        >
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.productSection}>
                    <LazyImage {...styles.img} src={productImage} altText="product image" />
                    <StyledWrapper {...styles.descriptionSection}>
                        {productBrand && <Typography {...styles.brandLabel}>{productBrand.name}</Typography>}
                        <Typography {...styles.productName}>{productName}</Typography>

                        <ProductPartNumbers
                            productNumber={productSku}
                            customerProductNumber={sampleProduct.customerProductNumber}
                            manufacturerItem={sampleProduct.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                        <Typography {...styles.infoMessage}>{siteMessage("Sample_Compliances_Message")}</Typography>
                    </StyledWrapper>
                </StyledWrapper>

                <SampleRequestForm
                    productName={productName}
                    productSku={productSku}
                    productBrand={productBrand?.name || ""}
                    isModalOpen={isModalOpen}
                    toggleModal={toggleModal}
                    extendedStyles={styles.sampleRequestFormStyles}
                    redirectOnSubmit={redirectOnSubmit}
                />
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps)(AuthenticatedSampleRequestForm);